import {useDialogContext, useDialogStore} from '@ariakit/react'
import {
    Dialog,
    DialogBody,
    DialogDismiss,
    DialogFooter,
    DialogHeader,
    DialogHeading,
    DialogProvider
} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Text} from '@/components/ui/text/Text'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Trans, useTranslation} from '@/translations/i18n'
import {toast} from '@/utilities/toast/toast'
import {useCancelSiarExport} from '@/features/projects/siar-bulk-export/queries/useCancelSiarExport'
import {SiarExport} from '@/features/projects/siar-bulk-export/types'

export const CancelExportDialog = ({
    siarExport,
    isOpen,
    onClose
}: {
    siarExport: SiarExport
    isOpen: boolean
    onClose: () => void
}) => {
    const dialogStore = useDialogStore({
        open: isOpen,
        setOpen: nextOpen => {
            if (!nextOpen) onClose()
        }
    })

    return (
        <DialogProvider store={dialogStore}>
            <Dialog width="400px">
                {isOpen && <CancelExportDialogContent siarExport={siarExport} onClose={onClose} />}
            </Dialog>
        </DialogProvider>
    )
}

const CancelExportDialogContent = ({siarExport, onClose}: {siarExport: SiarExport; onClose: () => void}) => {
    const {t} = useTranslation()
    const dialog = useDialogContext()

    const mutation = useCancelSiarExport({
        onSuccess: () => {
            toast.success(t('projects:bulk_siar_export_cancel_success'))
            dialog?.setOpen(false)
            onClose()
        },
        onError: () => {
            toast.error(t('errors:default'))
        }
    })

    return (
        <>
            <DialogHeader>
                <DialogHeading>{t('projects:bulk_siar_export_cancel_title')}</DialogHeading>
                <DialogDismiss />
            </DialogHeader>
            <DialogBody>
                <Flexbox direction="column" gap={3}>
                    <Text size="textSm" palette="neutral" shade="700">
                        <Trans
                            i18nKey="projects:bulk_siar_export_cancel_description"
                            values={{name: siarExport.fileName}}
                            components={{b: <b />}}
                        />
                    </Text>
                    <Text size="textXs" palette="neutral" shade="600">
                        {t('projects:bulk_siar_export_action_irreversible')}
                    </Text>
                </Flexbox>
            </DialogBody>
            <DialogFooter>
                <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
                    {t('commons:close')}
                </Button>
                <Button
                    variant="primaryDanger"
                    onClick={() => mutation.mutate({id: siarExport.id})}
                    disabled={mutation.isPending}
                >
                    {t('projects:bulk_siar_export_cancel')}
                    {mutation.isPending && <Spinner />}
                </Button>
            </DialogFooter>
        </>
    )
}
